import { useMemo, useState } from "react";
import { Search, FileText, GraduationCap, FolderKanban, Calculator, Users, ChevronDown } from "lucide-react";

const ICONS = { FileText, GraduationCap, FolderKanban, Calculator, Users };

export default function Sidebar({ categories, documents, selectedId, onSelect }) {
  const [search, setSearch] = useState("");
  const [openCat, setOpenCat] = useState(null);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? documents.filter(
            (d) => d.title.toLowerCase().includes(q) || d.description.toLowerCase().includes(q)
          )
        : documents,
    [documents, q]
  );

  const docsByCat = (catId) => filtered.filter((d) => d.category === catId);

  return (
    <aside className="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm lg:sticky lg:top-20">
      <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">
        1. Odaberite dokument
      </p>
      <div className="relative flex items-center rounded-xl border border-slate-200 bg-slate-50 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all mb-4">
        <Search className="w-4 h-4 text-slate-400 ml-3" />
        <input
          data-testid="document-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pretražite dokumente…"
          className="w-full bg-transparent px-2.5 py-2.5 text-sm outline-none placeholder:text-slate-400"
        />
      </div>
      <div className="space-y-2.5">
        {categories.map((cat) => {
          const Icon = ICONS[cat.icon] || FileText;
          const docs = docsByCat(cat.id);
          const isOpen = q ? docs.length > 0 : openCat === cat.id;
          if (q && docs.length === 0) return null;
          return (
            <div key={cat.id} className="border border-slate-200/80 rounded-xl overflow-hidden">
              <button
                data-testid={`category-tab-${cat.id}`}
                aria-expanded={isOpen}
                onClick={() => setOpenCat(openCat === cat.id ? null : cat.id)}
                className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-all ${
                  isOpen ? "bg-blue-50/70" : "bg-slate-50 hover:bg-slate-100"
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isOpen ? "bg-blue-700 text-white" : "bg-white border border-slate-200 text-blue-700"}`}>
                  <Icon className="w-4.5 h-4.5 w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{cat.title}</p>
                  <p className="text-xs text-slate-500 truncate">{cat.description}</p>
                </div>
                <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-lg text-xs font-semibold">
                  {docs.length}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="p-2 space-y-1.5 bg-white">
                  {docs.map((doc) => (
                    <button
                      key={doc.id}
                      data-testid={`document-card-${doc.id}`}
                      onClick={() => onSelect(doc)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                        selectedId === doc.id
                          ? "bg-blue-700 border-blue-700 text-white shadow-sm"
                          : "bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 text-slate-800"
                      }`}
                    >
                      <p className="text-sm font-semibold">{doc.title}</p>
                      <p className={`text-xs mt-0.5 ${selectedId === doc.id ? "text-blue-100" : "text-slate-500"}`}>
                        {doc.description}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {q && filtered.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-6" data-testid="search-no-results">
            Nema dokumenata za &quot;{search}&quot;.
          </p>
        )}
      </div>
    </aside>
  );
}
